import { get, isObject, isString, omit } from 'lodash';
import { GET_STUDY_SUMMARY_ENDPOINT } from '../../../common/config/api_endpoints';
import {
  createAPILifecycleActions,
  createAPILifecycleActionTypes,
} from '../../../redux/actions/action_utils';
import createAmplifyAPICallEpic from '../../../redux/epics/utils';
import calculateAdditionalBenefits from '../utils/study_summary_utils';
import STUDY_SUMMARY_TYPES from '../constants/studySummaries';

const GET_STUDY_SUMMARY_ACTION_TYPES = createAPILifecycleActionTypes(
  'GET_STUDY_SUMMARY_ACTION_TYPES',
);
export const getStudySummaryActions = createAPILifecycleActions(
  GET_STUDY_SUMMARY_ACTION_TYPES.NAME,
);

const SUMMARY_PROCESSORS = {
  QRE: summary => calculateAdditionalBenefits(JSON.parse(summary)),
  default: summary => JSON.parse(summary),
};

const processSummaryData = (summaryType, summary) => {
  if (!isString(summaryType) || !isString(summary)) return null;

  try {
    const processor = SUMMARY_PROCESSORS[summaryType] || SUMMARY_PROCESSORS.default;
    return { [summaryType]: processor(summary) };
  } catch (error) {
    return null;
  }
};

// Returns the first survey type found or null.
// Note: If multiple survey types exist, only the first one is used.
// Only 1 surveyType is returned in API response as of now
const extractSurveyType = data => {
  const surveyTypes = Object.keys(data.aggBySurveyType || {});
  return surveyTypes.length > 0 ? surveyTypes[0] : null;
};

const mergeStatusCounts = (existing = {}, incoming = {}) => {
  if (!incoming) return existing;
  return {
    Submitted: (existing.Submitted || 0) + (incoming.Submitted || 0),
    InProgress: (existing.InProgress || 0) + (incoming.InProgress || 0),
    Created: (existing.Created || 0) + (incoming.Created || 0),
  };
};

const mergeSurveyData = (existingData, newData) => {
  if (!existingData) return newData;
  if (!newData) return existingData;

  const surveyType = extractSurveyType(newData);

  return {
    // Preserve any prior metadata; the explicit keys below then override it.
    // (A previous version spread `existingData.surveyTypes` here, which leaked
    // each surveyType key as a junk top-level sibling.)
    ...existingData,
    // Store individual survey types for detailed calculations
    surveyTypes: {
      ...existingData.surveyTypes,
      ...(surveyType && { [surveyType]: newData }),
    },
    // Merge aggregated status counts for overview - only for survey data
    aggByStatus: mergeStatusCounts(existingData.aggByStatus, newData.aggByStatus),
    // Preserve metadata from latest response
    clientId: newData.clientId || existingData.clientId,
    country: newData.country || existingData.country,
    studyPeriod: newData.studyPeriod || existingData.studyPeriod,
  };
};

const updateStateWithNewData = (state, summaryType, newData) => {
  const existingData = state.data?.[summaryType];

  // Only use survey-specific merging for SURVEYS summary type
  const mergedData =
    summaryType === STUDY_SUMMARY_TYPES.SURVEYS.key
      ? mergeSurveyData(existingData || {}, newData)
      : newData;

  return {
    ...state,
    isFetching: false,
    fetchingByType: { ...state.fetchingByType, [summaryType]: false },
    // A successful load clears this type's error so a stale failure banner
    // (e.g. a prior SEASON/SURVEYS error) doesn't linger after data arrives.
    errorByType: { ...state.errorByType, [summaryType]: null },
    data: {
      ...(state.data || {}),
      [summaryType]: mergedData,
    },
    error: null,
  };
};

const initialState = {
  isFetching: false,
  // Per-summaryType fetching flags. The shared `isFetching` boolean above is
  // toggled by every request (SEASON/QRE/SURVEYS) and clears on the FIRST
  // response, so it can't tell one type's edges from another's. Concurrent
  // consumers (SurveysCard, QRECard) read their own type's flag here instead.
  // `isFetching` is preserved for other consumers (SurveysTab, QRETab).
  fetchingByType: {},
  data: null,
  error: null,
  // Per-summaryType error. The shared `error` above is overwritten by whichever
  // request fails last (SEASON/QRE/SURVEYS), so it can't tell one type's failure
  // from another's. Concurrent consumers read their own type's error here.
  errorByType: {},
};

export default function studySummaryReducer(state = initialState, { type, payload }) {
  switch (type) {
    case GET_STUDY_SUMMARY_ACTION_TYPES.BEGIN: {
      const summaryType = get(payload, 'body.summaryType');
      return {
        ...state,
        isFetching: true,
        fetchingByType: summaryType
          ? { ...state.fetchingByType, [summaryType]: true }
          : state.fetchingByType,
        // Clear this type's prior error at the start of a new request so a
        // consumer can treat a lingering errorByType[type] as "the request that
        // just settled failed".
        errorByType: summaryType
          ? { ...state.errorByType, [summaryType]: null }
          : state.errorByType,
      };
    }

    case GET_STUDY_SUMMARY_ACTION_TYPES.SUCCESS: {
      // No summaryType to scope on a malformed envelope; at least clear the shared
      // flag so a consumer isn't left spinning forever.
      if (!isObject(payload)) return { ...state, isFetching: false };

      const responseType = payload.summaryType;
      if (payload.summary == null) {
        // No record for this type (e.g. a survey type with no surveys): settle with no data.
        return {
          ...state,
          isFetching: false,
          fetchingByType: responseType
            ? { ...state.fetchingByType, [responseType]: false }
            : state.fetchingByType,
        };
      }
      const processedSummary = processSummaryData(responseType, payload.summary);
      if (!processedSummary) {
        // Unparseable summary: settle this type's fetching flag and record an error
        // so consumers waiting on the true->false edge don't hang.
        const message = 'Malformed study summary response';
        return {
          ...state,
          isFetching: false,
          fetchingByType: responseType
            ? { ...state.fetchingByType, [responseType]: false }
            : state.fetchingByType,
          errorByType: responseType
            ? { ...state.errorByType, [responseType]: message }
            : state.errorByType,
          error: message,
        };
      }

      const [summaryType, newData] = Object.entries(processedSummary)[0];
      return updateStateWithNewData(state, summaryType, newData);
    }

    case GET_STUDY_SUMMARY_ACTION_TYPES.FAILURE: {
      // Keep already-loaded data: one summary type failing must not wipe the others.
      // The epic wraps the failure as { error, summaryType } so the per-type flag
      // can be cleared here; fall back to the raw payload if that key is absent.
      const summaryType = get(payload, 'summaryType');
      const failureError = get(payload, 'error', payload);
      return {
        ...state,
        isFetching: false,
        fetchingByType: summaryType
          ? { ...state.fetchingByType, [summaryType]: false }
          : state.fetchingByType,
        errorByType: summaryType
          ? { ...state.errorByType, [summaryType]: failureError }
          : state.errorByType,
        error: failureError,
      };
    }

    case GET_STUDY_SUMMARY_ACTION_TYPES.CLEAR:
      // A keyed CLEAR (e.g. CLEAR('SURVEYS')) drops that slice and its error so a
      // remounting card starts from a clean slate; an unkeyed CLEAR resets all.
      if (isString(payload)) {
        return {
          ...state,
          data: isObject(state.data) ? omit(state.data, payload) : null,
          errorByType: { ...state.errorByType, [payload]: null },
        };
      }
      return { ...state, data: null, errorByType: {} };
    default:
      return state;
  }
}

export const getStudySummaryEpic = createAmplifyAPICallEpic({
  actionType: GET_STUDY_SUMMARY_ACTION_TYPES.BEGIN,
  api: GET_STUDY_SUMMARY_ENDPOINT,
  successCallback: (response, store, action) => {
    const summaryType = get(action, 'payload.body.summaryType');
    if (isObject(response.body)) return [getStudySummaryActions.SUCCESS(response.body)];
    return [
      getStudySummaryActions.FAILURE({
        error: response.statusMessage || 'Malformed study summary response',
        summaryType,
      }),
    ];
  },
  errorCallback: (error, store, action) => {
    // Thread the request's summaryType through so the reducer can clear the
    // matching per-type fetching flag (a failed request never reaches SUCCESS).
    const summaryType = get(action, 'payload.body.summaryType');
    // Amplify rejects non-2xx; a 404 means this type has no record (e.g. no surveys).
    if (get(error, 'response.status') === 404) {
      return [getStudySummaryActions.SUCCESS({ summaryType })];
    }
    return [getStudySummaryActions.FAILURE({ error, summaryType })];
  },
});
